import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import SignalsCountriesTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsCountriesTooltip/SignalsCountriesTooltip";
import type TrafficCountrySignalRowDto from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficCountrySignalRowDto";
import type TrafficSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalSubType";
import type SignalsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/SignalsTooltipViewModel";
import createTrafficSignalsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/traffic/factory";
import { sortNumbersDesc } from "app/pages/sales-intelligence/utils/sort";

interface ISignalTrafficCountriesTooltipProps<V extends LeadsSearchRow> {
    value?: readonly TrafficCountrySignalRowDto[];
    row: V;
    signalSubType: TrafficSignalSubType;
    onViewAll: () => void;
    redirectLink: URL["href"];
    filters: { readonly metric: string };
}

export const SignalTrafficCountriesTooltip = <V extends LeadsSearchRow>({
    children,
    value,
    signalSubType,
    onViewAll,
    redirectLink,
    filters,
}: PropsWithChildren<ISignalTrafficCountriesTooltipProps<V>>) => {
    const sortedCountries = useMemo<TrafficCountrySignalRowDto[]>(() => {
        if (!value || value.length === 0) {
            return [];
        }

        return value.slice().sort((a, b) => sortNumbersDesc(a.value, b.value));
    }, [value]);

    const createViewModel = (itemsLimit: number): SignalsTooltipViewModel =>
        createTrafficSignalsTooltipViewModel({
            metric: filters.metric,
            countries: sortedCountries,
            itemsLimit,
        });

    if (sortedCountries.length === 0) {
        return <>{children}</>;
    }

    return (
        <SignalsCountriesTooltip
            signalSubType={signalSubType}
            redirectLink={redirectLink}
            createViewModel={createViewModel}
            onViewAll={onViewAll}
        >
            <>{children}</>
        </SignalsCountriesTooltip>
    );
};
